import React, { useEffect, useRef, useState } from 'react';
import { Link, useLocation, useNavigate, Outlet } from 'react-router-dom';
import { useTheme } from '../../theme/ThemeContext';
import { useNavActions } from '../../context/NavActionsContext';
import { useAuth } from '../../context/AuthContext';
import Sidebar from './Sidebar';
import LegalFooter from './LegalFooter';
import GlobalSearchBar from '../ui/GlobalSearchBar';

function useIsMobile(breakpoint = 640) {
  const [isMobile, setIsMobile] = useState(() => window.innerWidth < breakpoint);
  useEffect(() => {
    const handler = () => setIsMobile(window.innerWidth < breakpoint);
    window.addEventListener('resize', handler);
    return () => window.removeEventListener('resize', handler);
  }, [breakpoint]);
  return isMobile;
}

function useViewportWide(breakpoint = 1200) {
  const [wide, setWide] = useState(() => window.innerWidth >= breakpoint);
  useEffect(() => {
    const handler = () => setWide(window.innerWidth >= breakpoint);
    window.addEventListener('resize', handler);
    return () => window.removeEventListener('resize', handler);
  }, [breakpoint]);
  return wide;
}

/* ── Sun icon ── */
function SunIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="5"/>
      <line x1="12" y1="1" x2="12" y2="3"/><line x1="12" y1="21" x2="12" y2="23"/>
      <line x1="4.22" y1="4.22" x2="5.64" y2="5.64"/><line x1="18.36" y1="18.36" x2="19.78" y2="19.78"/>
      <line x1="1" y1="12" x2="3" y2="12"/><line x1="21" y1="12" x2="23" y2="12"/>
      <line x1="4.22" y1="19.78" x2="5.64" y2="18.36"/><line x1="18.36" y1="5.64" x2="19.78" y2="4.22"/>
    </svg>
  );
}

/* ── Moon icon ── */
function MoonIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z"/>
    </svg>
  );
}

/* ── User icon ── */
function UserIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/>
      <circle cx="12" cy="7" r="4"/>
    </svg>
  );
}

/* ── Back arrow SVG ── */
function BackArrowIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="15 18 9 12 15 6"/>
    </svg>
  );
}

/* ── Hamburger / Close SVG ── */
function HamburgerIcon() {
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
      <line x1="3" y1="6" x2="21" y2="6"/>
      <line x1="3" y1="12" x2="21" y2="12"/>
      <line x1="3" y1="18" x2="21" y2="18"/>
    </svg>
  );
}

function CloseIcon() {
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
      <line x1="18" y1="6" x2="6" y2="18"/>
      <line x1="6" y1="6" x2="18" y2="18"/>
    </svg>
  );
}

function AppShell({ children }) {
  const { themeName, setThemeName } = useTheme();
  const location = useLocation();
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const isWide   = useViewportWide(1200);

  const [menuOpen, setMenuOpen]       = useState(false); // mobile hamburger
  const [accountOpen, setAccountOpen] = useState(false); // desktop account dropdown

  const menuRef    = useRef(null);
  const accountRef = useRef(null);

  const { actions, navRightActions } = useNavActions();
  const { user, logout } = useAuth();

  async function handleLogout() {
    await logout();
    navigate('/login');
  }

  const toggleTheme = () => setThemeName(themeName === 'dark' ? 'light' : 'dark');

  // Close menus on navigation
  useEffect(() => { setMenuOpen(false); setAccountOpen(false); }, [location.pathname]);

  // Close mobile menu on outside click
  useEffect(() => {
    if (!menuOpen) return;
    const handler = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) setMenuOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [menuOpen]);

  // Close account dropdown on outside click
  useEffect(() => {
    if (!accountOpen) return;
    const handler = (e) => {
      if (accountRef.current && !accountRef.current.contains(e.target)) setAccountOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [accountOpen]);

  const isHome      = location.pathname === '/';
  const isPortfolio = location.pathname === '/portfolios' || location.pathname.startsWith('/portfolio/');

  // Sidebar shows for all desktop users (logged-in or not) — Stocks, Crypto,
  // Portfolios and Profile are now visible to everyone including guests.
  const showSidebar = !isMobile;

  // Label for the back-arrow button in the header (desktop & mobile back-mode)
  const backTarget = getBackTarget(location.pathname);
  const showAdSlot = isWide && isInDevelopmentRoute(location.pathname);

  // Show back arrow on any page except the homepage.
  const showBack = location.pathname !== '/';

  // ── NavRight icons (gear, etc.) shared between desktop & mobile back mode ──
  const navRightButtons = navRightActions.map((action) => (
    <button
      key={action.id}
      type="button"
      onClick={action.onClick}
      style={iconBtnStyle}
      title={action.title}
      aria-label={action.title}
    >
      {action.renderIcon ? action.renderIcon() : null}
    </button>
  ));

  return (
    <div style={appStyle}>
      <header style={headerStyle}>
        <div
          className="rp-header-inner"
          style={
            !isMobile ? headerInnerStyle :
            showBack   ? headerInnerBackStyle :
            mobileHeaderInnerStyle
          }
        >
          {!isMobile ? (
            /* ══════════════════ DESKTOP ══════════════════ */
            <>
              {/* LEFT – brand or back arrow */}
              {showBack ? (
                <div style={backLeftStyle}>
                  <button
                    type="button"
                    onClick={() => backTarget?.to ? navigate(backTarget.to) : navigate(-1)}
                    style={backBtnStyle}
                    aria-label={backTarget ? `Back to ${backTarget.label}` : 'Go back'}
                  >
                    <BackArrowIcon />
                    {backTarget?.label && <span style={backLabelStyle}>{backTarget.label}</span>}
                  </button>
                  <Link to="/" style={brandRowStyle} className="rp-brand">
                    <img src="/favicon-32x32.png" alt="" width="24" height="24" style={brandLogoStyle} />
                    <span style={brandStyle}>Inverow</span>
                  </Link>
                </div>
              ) : (
                <Link to="/" style={brandRowStyle} className="rp-brand">
                  <img src="/favicon-32x32.png" alt="" width="28" height="28" style={brandLogoStyle} />
                  <div style={brandBlockStyle}>
                    <span style={brandStyle}>Inverow</span>
                    <span style={subtitleStyle}>Investor toolkit</span>
                  </div>
                </Link>
              )}

              {/* CENTER – global asset search (all logged-in users) */}
              {user ? <GlobalSearchBar /> : <div />}

              {/* RIGHT – navRight icons + theme toggle + account */}
              <div style={rightGroupStyle}>
                {navRightButtons}

                <button
                  type="button"
                  onClick={toggleTheme}
                  style={iconBtnStyle}
                  title={themeName === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'}
                >
                  {themeName === 'dark' ? <SunIcon /> : <MoonIcon />}
                </button>

                {user ? (
                  <div ref={accountRef} style={{ position: 'relative' }}>
                    <button
                      type="button"
                      onClick={() => setAccountOpen((v) => !v)}
                      style={{ ...iconBtnStyle, ...(accountOpen ? iconBtnActiveStyle : {}) }}
                      aria-label="Account menu"
                      title={user.email}
                    >
                      <UserIcon />
                    </button>

                    {accountOpen && (
                      <div style={accountDropdownStyle}>
                        <div style={accountEmailStyle}>{user.email}</div>
                        <div style={dropdownDividerStyle} />
                        <button
                          type="button"
                          onClick={() => { navigate('/profile'); setAccountOpen(false); }}
                          style={dropdownItemStyle}
                        >
                          Profile settings
                        </button>
                        {user.role === 'Admin' && (
                          <button
                            type="button"
                            onClick={() => { navigate('/admin'); setAccountOpen(false); }}
                            style={{ ...dropdownItemStyle, color: 'var(--accent)' }}
                          >
                            Admin tools
                          </button>
                        )}
                        <div style={dropdownDividerStyle} />
                        <button
                          type="button"
                          onClick={() => { handleLogout(); setAccountOpen(false); }}
                          style={{ ...dropdownItemStyle, color: '#f87171' }}
                        >
                          Sign out
                        </button>
                      </div>
                    )}
                  </div>
                ) : (
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <Link to="/login"    style={authBtnOutlineStyle}>Sign in</Link>
                    <Link to="/register" style={authBtnStyle}>Sign up</Link>
                  </div>
                )}
              </div>
            </>

          ) : showBack ? (
            /* ══════════════════ MOBILE – back mode ══════════════════ */
            <>
              <div style={backLeftStyle}>
                <button
                  type="button"
                  onClick={() => backTarget?.to ? navigate(backTarget.to) : navigate(-1)}
                  style={backBtnStyle}
                  aria-label={backTarget ? `Back to ${backTarget.label}` : 'Go back'}
                >
                  <BackArrowIcon />
                  {backTarget?.label && <span style={backLabelStyle}>{backTarget.label}</span>}
                </button>
                <Link to="/" style={brandRowStyle} className="rp-brand">
                  <img src="/favicon-32x32.png" alt="" width="22" height="22" style={brandLogoStyle} />
                </Link>
              </div>

              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                {navRightButtons}

                <div ref={menuRef} style={{ position: 'relative' }}>
                  <button type="button" onClick={() => setMenuOpen((v) => !v)} style={iconBtnStyle} aria-label="Toggle menu">
                    {menuOpen ? <CloseIcon /> : <HamburgerIcon />}
                  </button>
                  {menuOpen && <MobileMenu {...{ isHome, isPortfolio, themeName, toggleTheme, actions, user, handleLogout, setMenuOpen }} />}
                </div>
              </div>
            </>

          ) : (
            /* ══════════════════ MOBILE – normal mode ══════════════════ */
            <>
              <Link to="/" style={brandRowStyle} className="rp-brand">
                <img src="/favicon-32x32.png" alt="" width="26" height="26" style={brandLogoStyle} />
                <div style={brandBlockStyle}>
                  <span style={brandStyle}>Inverow</span>
                  <span style={subtitleStyle}>Investor toolkit</span>
                </div>
              </Link>

              <div ref={menuRef} style={{ position: 'relative' }}>
                <button type="button" onClick={() => setMenuOpen((v) => !v)} style={iconBtnStyle} aria-label="Toggle menu">
                  {menuOpen ? <CloseIcon /> : <HamburgerIcon />}
                </button>
                {menuOpen && <MobileMenu {...{ isHome, isPortfolio, themeName, toggleTheme, actions, user, handleLogout, setMenuOpen }} />}
              </div>
            </>
          )}
        </div>
      </header>

      <div style={bodyWrapStyle}>
        {showSidebar ? (
          <div style={withSidebarLayout}>
            <Sidebar />
            <main style={showAdSlot ? mainWithSidebarStyle : mainWithSidebarWideStyle}>{children ?? <Outlet />}</main>
            {showAdSlot && <AdSlot />}
          </div>
        ) : (
          <main style={mainStyle}>{children ?? <Outlet />}</main>
        )}
      </div>
      <LegalFooter />
    </div>
  );
}

/* ── Right-side ad slot (placeholder, admin-only for now) ── */
function AdSlot() {
  return (
    <aside style={adSlotStyle}>
      <div style={adSlotInnerStyle}>reklama</div>
    </aside>
  );
}

const adSlotStyle = {
  width: '220px', flexShrink: 0,
  padding: '16px 8px',
  borderLeft: '1px solid var(--border)',
  background: 'var(--surface)',
  height: 'calc(100vh - 53px)',
  boxSizing: 'border-box',
  position: 'sticky',
  top: '53px',
  alignSelf: 'flex-start',
  overflowY: 'auto',
};

const adSlotInnerStyle = {
  width: '100%', height: '100%',
  display: 'flex', alignItems: 'center', justifyContent: 'center',
  border: '2px dashed var(--border)',
  borderRadius: '12px',
  color: 'var(--text-secondary)',
  fontSize: '14px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.6px',
};

/* ── Mobile dropdown menu ── */
function MobileMenu({ isHome, isPortfolio, themeName, toggleTheme, actions, user, handleLogout, setMenuOpen }) {
  return (
    <div style={mobileMenuStyle}>
      <Link to="/" style={{ ...mobileNavLinkStyle, ...(isHome ? activeMobileLinkStyle : {}) }}>Home</Link>
      <Link to="/stocks"     style={mobileNavLinkStyle}>Stocks</Link>
      <Link to="/crypto"     style={mobileNavLinkStyle}>Crypto</Link>
      <Link to="/portfolios" style={{ ...mobileNavLinkStyle, ...(isPortfolio ? activeMobileLinkStyle : {}) }}>Portfolios</Link>

      <div style={mobileDividerStyle} />

      <button type="button" onClick={() => { toggleTheme(); setMenuOpen(false); }} style={mobileActionStyle}>
        {themeName === 'dark' ? <><SunIcon /><span>Light mode</span></> : <><MoonIcon /><span>Dark mode</span></>}
      </button>

      {actions.length > 0 && (
        <>
          <div style={mobileDividerStyle} />
          {actions.map((action) => (
            <button key={action.id} type="button" onClick={() => { action.onClick(); setMenuOpen(false); }} style={mobileActionStyle}>
              <span>{action.label}</span>
            </button>
          ))}
        </>
      )}

      <div style={mobileDividerStyle} />

      {user ? (
        <>
          <span style={mobileEmailStyle}>{user.email}</span>
          <Link to="/profile" style={mobileActionStyle} onClick={() => setMenuOpen(false)}>
            Profile settings
          </Link>
          <Link to="/settings" style={mobileActionStyle} onClick={() => setMenuOpen(false)}>Settings</Link>
          {user.role === 'Admin' && (
            <>
              <div style={mobileDividerStyle} />
              <Link to="/admin"            style={{ ...mobileActionStyle, color: 'var(--accent)' }} onClick={() => setMenuOpen(false)}>Statistics</Link>
              <Link to="/admin/landing"    style={{ ...mobileActionStyle, color: 'var(--accent)' }} onClick={() => setMenuOpen(false)}>Landing preview</Link>
              <Link to="/admin/roadmap"    style={{ ...mobileActionStyle, color: 'var(--accent)' }} onClick={() => setMenuOpen(false)}>Roadmap editor</Link>
              <Link to="/admin/progress"   style={{ ...mobileActionStyle, color: 'var(--accent)' }} onClick={() => setMenuOpen(false)}>Progress</Link>
            </>
          )}
          <button type="button" onClick={() => { handleLogout(); setMenuOpen(false); }} style={{ ...mobileActionStyle, color: '#f87171' }}>
            Sign out
          </button>
        </>
      ) : (
        <>
          <Link to="/login"    style={mobileNavLinkStyle} onClick={() => setMenuOpen(false)}>Sign in</Link>
          <Link to="/register" style={{ ...mobileNavLinkStyle, color: 'var(--primary)', fontWeight: 700 }} onClick={() => setMenuOpen(false)}>Sign up</Link>
        </>
      )}
    </div>
  );
}

/* ── Styles ── */
const appStyle    = { minHeight: '100vh', overflowX: 'clip', width: '100%', display: 'flex', flexDirection: 'column' };
const bodyWrapStyle = { flex: 1, display: 'flex', flexDirection: 'column' };
const headerStyle = { position: 'sticky', top: 0, zIndex: 100, backdropFilter: 'var(--blur)', WebkitBackdropFilter: 'var(--blur)', background: 'var(--header-bg)', borderBottom: '1px solid var(--border)' };

/* Desktop: 3-column grid → brand/back | nav (center) | actions */
const headerInnerStyle = {
  width: '100%',
  padding: '8px 16px',
  display: 'grid',
  gridTemplateColumns: '1fr auto 1fr',
  alignItems: 'center',
  gap: '16px',
  boxSizing: 'border-box',
};

/* Mobile back-mode: simple flex */
const headerInnerBackStyle = {
  width: '100%',
  padding: '14px 12px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  boxSizing: 'border-box',
};

/* Mobile normal mode: brand left, hamburger right */
const mobileHeaderInnerStyle = {
  width: '100%',
  padding: '6px 12px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  boxSizing: 'border-box',
};

const backLeftStyle    = { display: 'flex', alignItems: 'center', gap: '12px' };
const brandRowStyle    = { display: 'inline-flex', alignItems: 'center', gap: '10px', textDecoration: 'none' };
const brandLogoStyle   = { display: 'block', borderRadius: '6px' };
const backBtnStyle     = { display: 'inline-flex', alignItems: 'center', gap: '8px', background: 'rgba(255,255,255,0.12)', border: '1px solid rgba(255,255,255,0.22)', borderRadius: '10px', color: 'var(--header-text)', height: '36px', padding: '0 14px 0 10px', cursor: 'pointer', fontWeight: 700, fontSize: '14px' };
const backLabelStyle   = { color: 'var(--header-text)' };
const brandBlockStyle  = { display: 'flex', flexDirection: 'column', gap: '2px' };
const brandStyle       = { color: 'var(--header-text)', textDecoration: 'none', fontSize: '20px', fontWeight: 800, lineHeight: 1 };
const subtitleStyle    = { color: 'var(--header-text)', fontSize: '12px', opacity: 0.6 };

/* Right group: navRight icons + theme toggle + account icon, aligned to the right */
const rightGroupStyle  = { display: 'flex', alignItems: 'center', gap: '8px', justifyContent: 'flex-end' };

const iconBtnStyle = {
  background: 'rgba(255,255,255,0.12)',
  border: '1px solid rgba(255,255,255,0.22)',
  borderRadius: '10px',
  color: 'var(--header-text)',
  width: '36px',
  height: '36px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  cursor: 'pointer',
  padding: 0,
  flexShrink: 0,
};

const iconBtnActiveStyle = {
  background: 'rgba(255,255,255,0.22)',
  border: '1px solid rgba(255,255,255,0.38)',
};

/* Account dropdown */
const accountDropdownStyle = {
  position: 'absolute',
  top: 'calc(100% + 8px)',
  right: 0,
  background: 'var(--surface-strong)',
  border: '1px solid var(--border)',
  borderRadius: '14px',
  boxShadow: '0 8px 32px rgba(0,0,0,0.45)',
  minWidth: '200px',
  padding: '8px',
  zIndex: 200,
};
const accountEmailStyle = {
  padding: '8px 12px',
  fontSize: '12px',
  color: 'var(--text-secondary)',
  wordBreak: 'break-all',
};
const dropdownDividerStyle = { height: '1px', background: 'var(--border)', margin: '4px 0' };
const dropdownItemStyle    = {
  width: '100%',
  padding: '6px 12px',
  background: 'transparent',
  border: 'none',
  borderRadius: '8px',
  color: 'var(--text-primary)',
  fontWeight: 600,
  fontSize: '14px',
  cursor: 'pointer',
  textAlign: 'left',
};

/* Auth buttons (unauthenticated desktop) */
const authBtnStyle        = { display: 'inline-flex', alignItems: 'center', height: '36px', padding: '0 18px', borderRadius: '10px', background: 'rgba(255,255,255,0.9)', color: '#1a1a2e', textDecoration: 'none', fontWeight: 700, fontSize: '13px', border: 'none', cursor: 'pointer', whiteSpace: 'nowrap' };
const authBtnOutlineStyle = { display: 'inline-flex', alignItems: 'center', height: '36px', padding: '0 18px', borderRadius: '10px', background: 'rgba(255,255,255,0.12)', border: '1px solid rgba(255,255,255,0.28)', color: 'var(--header-text)', textDecoration: 'none', fontWeight: 600, fontSize: '13px', cursor: 'pointer', whiteSpace: 'nowrap' };

/* Mobile menu */
const mobileMenuStyle   = { position: 'fixed', top: '52px', left: '12px', right: '12px', background: 'var(--surface-strong)', border: '1px solid var(--border)', borderRadius: '16px', boxShadow: '0 8px 32px rgba(0,0,0,0.5)', zIndex: 200, padding: '10px', display: 'grid', gap: '4px' };
const mobileNavLinkStyle = { display: 'block', padding: '11px 14px', borderRadius: '10px', color: 'var(--text-primary)', textDecoration: 'none', fontWeight: 700, fontSize: '15px' };
const activeMobileLinkStyle = { background: 'var(--primary)', color: '#fff' };
const mobileDividerStyle = { height: '1px', background: 'var(--border)', margin: '6px 0' };
const mobileActionStyle  = { display: 'flex', alignItems: 'center', gap: '10px', padding: '11px 14px', borderRadius: '10px', background: 'transparent', border: 'none', color: 'var(--text-primary)', cursor: 'pointer', fontWeight: 700, fontSize: '15px', width: '100%', textAlign: 'left' };
const mobileEmailStyle   = { padding: '6px 14px', fontSize: '12px', color: 'var(--text-secondary)', display: 'block', wordBreak: 'break-all' };

const mainStyle = { maxWidth: '1400px', margin: '0 auto', padding: '24px 16px 40px' };

const withSidebarLayout = { display: 'flex', alignItems: 'flex-start', width: '100%', flex: 1 };
const mainWithSidebarStyle     = { flex: 1, minWidth: 0, padding: '28px 18px 40px' };
const mainWithSidebarWideStyle = { flex: 1, minWidth: 0, padding: '28px 40px 40px' };

/**
 * Compute back-arrow label + target for the header, based on the current path.
 * Returning { label, to } makes the arrow navigate to a specific URL with a label,
 * which is clearer than `history.back()`.
 */
// "In Development" routes get the ad slot. Stocks/Crypto excluded — no ads there yet.
function isInDevelopmentRoute(_pathname) {
  return false;
}

function getBackTarget(pathname) {
  if (pathname === '/portfolios')                 return { label: 'Home',       to: '/' };
  if (pathname === '/portfolios/new')             return { label: 'Portfolios', to: '/portfolios' };
  if (pathname.startsWith('/portfolios/demo/'))   return { label: 'Portfolios', to: '/portfolios' };
  if (pathname.startsWith('/portfolio/')) {
    const parts = pathname.split('/').filter(Boolean); // ['portfolio', slug, ...]
    if (parts.length >= 3) {
      // /portfolio/:slug/:something → back to the portfolio dashboard
      return { label: 'Portfolio', to: `/portfolio/${parts[1]}` };
    }
    // /portfolio/:slug → back to Portfolios list
    return { label: 'Portfolios', to: '/portfolios' };
  }
  if (pathname.startsWith('/admin/users/'))       return { label: 'Admin tools', to: '/admin' };
  if (pathname.startsWith('/admin/'))             return { label: 'Admin tools', to: '/admin' };
  if (pathname === '/admin')                      return { label: 'Home',       to: '/' };
  if (pathname === '/profile')                    return { label: 'Home',       to: '/' };
  // Stocks & Crypto workspace: detail pages and sub-pages go back to the
  // workspace overview, not all the way to Home — keeps the user in context.
  if (pathname.startsWith('/stocks/') && pathname !== '/stocks') return { label: 'Stocks', to: '/stocks' };
  if (pathname.startsWith('/crypto/') && pathname !== '/crypto') return { label: 'Crypto', to: '/crypto' };
  return { label: 'Home', to: '/' };
}

export default AppShell;
